import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  useCallback,
  type ComponentRef,
} from 'react';

import OverlayCloseButton from '@/components/OverlayCloseButton';
import { useDeviceMatch } from '@/hooks/useDeviceMatch';
import { useTranslate } from '@/state/i18n';
import { useSheetDragToClose } from '@/hooks/useSheetDragToClose';
import { revealTokens, radiusTokens } from '@/theme/themePrimitives';

interface ModalHandleRef {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

export type ModalRef = ComponentRef<typeof Modal>;

export interface ModalProps extends Omit<DialogProps, 'open'> {
  /** Controlled mode for dialogs owned by a parent; the ref API remains available. */
  open?: boolean;
  title?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  showClose?: boolean;
  closeDisabled?: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
}

const Modal = forwardRef<ModalHandleRef | undefined, ModalProps>(
  (
    {
      title,
      children,
      actions,
      showClose,
      closeDisabled = false,
      onDismiss,
      onOpen,
      open: controlledOpen,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate('components.Modal');
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;

    const { isMobile } = useDeviceMatch();

    useImperativeHandle(ref, () => ({
      isOpen: open,
      open: () => {
        setInternalOpen(true);
        onOpen?.();
      },
      close: () => setInternalOpen(false),
    }));

    const handleClose = useCallback(() => {
      setInternalOpen(false);
      onDismiss?.();
    }, [onDismiss]);

    const handleDialogClose = useCallback(() => {
      if (closeDisabled) return;
      handleClose();
    }, [closeDisabled, handleClose]);

    // Signal 07/13: on a phone the modal rises as a bottom sheet with a handle that closes it on a
    // downward drag. A caller that asks for `fullScreen` keeps the full-screen dialog.
    const fullScreen = props.fullScreen ?? false;
    const sheet = isMobile && !fullScreen;
    const { offset, dragging, handleProps } = useSheetDragToClose(handleDialogClose);

    return (
      <Dialog
        {...props}
        open={open}
        fullWidth
        maxWidth={props.maxWidth ?? 'xs'}
        fullScreen={fullScreen}
        disableEscapeKeyDown={closeDisabled || Boolean(props.disableEscapeKeyDown)}
        onClose={handleDialogClose}
        slots={sheet ? { ...props.slots, transition: Slide } : props.slots}
        slotProps={
          sheet ? { ...props.slotProps, transition: { direction: 'up' } } : props.slotProps
        }
        sx={[
          ...(sheet ? [sx.sheet(offset, dragging)] : []),
          ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : []),
          ...(fullScreen ? [{ '& .MuiDialog-paper': { borderRadius: 0 } }] : []),
        ]}
      >
        {sheet ? <Box {...handleProps} aria-hidden sx={sx.handle} /> : null}
        <DialogTitle>{title}</DialogTitle>
        {/* Outside the title: inside it, "Fechar" became part of the dialog's accessible name. */}
        {showClose && (
          <OverlayCloseButton
            aria-label={t('close')}
            onClick={handleClose}
            disabled={closeDisabled}
            sx={sx.close}
          />
        )}
        <DialogContent>
          {typeof children === 'string' ? (
            <Typography variant="body2" color="text.secondary">
              {children}
            </Typography>
          ) : (
            children
          )}
        </DialogContent>
        {actions ? <DialogActions>{actions}</DialogActions> : null}
      </Dialog>
    );
  },
);

export default Modal;

const sx = {
  sheet: (offset: number, dragging: boolean) => ({
    '& .MuiDialog-container': { alignItems: 'flex-end' },
    '& .MuiDialog-paper': {
      m: 0,
      width: '100%',
      maxWidth: '100%',
      maxHeight: '85dvh',
      pt: 0,
      borderRadius: '28px 28px 0 0',
      boxShadow: revealTokens.sheetShadow,
      transform: offset ? `translateY(${offset}px) !important` : undefined,
      transition: dragging ? 'none !important' : undefined,
    },
  }),
  // 40 × 5 handle inside a 24px touch strip, as in the board.
  handle: {
    flexShrink: 0,
    alignSelf: 'stretch',
    height: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'grab',
    touchAction: 'none',
    '&::before': {
      content: '""',
      width: 40,
      height: 5,
      borderRadius: radiusTokens.pill,
      backgroundColor: revealTokens.sheetHandle,
    },
  },
  // Same close tile as the drawer; only its place in the title row lives here.
  close: {
    position: 'absolute',
    right: 14,
    top: 14,
  },
};
